<script setup lang="ts">

// add "crypto" props
import {type CoinPrices, coinPrices} from "@lib/external-data/coinPrices.ts";

import Callout from "@components/utils/Callout.vue";
import CoinConverterCalculator from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterCalculator.vue";
import CoinConverterRowCalculator
  from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterRowCalculator.vue";
import type {CoinMap} from "@lib/internal-data/coinMap.ts";
import {getCoinInfoBySymbol} from "@lib/getCoinInfo.ts";
import CoinConverterPriceInfo from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterPriceInfo.vue";
import CoinConverterPriceLabelTextInfo
  from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterPriceLabelTextInfo.vue";
import CoinConverterPriceTableInfo
  from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterPriceTableInfo.vue";
import {supportedQuoteCurrencies} from "@consts";
import type {ConverterRateResponse} from "@lib/interfaces/converter.ts";
import {quoteCurrencyConvertions} from "@lib/external-data/quoteCurrencyConvertions.ts";
import CoinConverterPriceConversionExamples
  from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterPriceConversionExamples.vue";
import CoinConverterRatePathList
  from "@components/sections/Cryptocurrencies/CoinConverter/CoinConverterRatePathList.vue";

const props = defineProps({
  crypto: {
    type: String,
    required: true
  },
  quote: {
    type: String,
    required: true
  }
});

// https://user.dipsway.cloud/prices/converter/binance POST
// body: {
//    "from":"BTC",
//    "to":"USDT",
//    "amount":1
// }

const normalizeUsd = (symbol: string) => {
  return symbol.toLowerCase()
      .replace('usdt', 'usd')
      .replace('usdc', 'usd')
      .replace('busd', 'usd');
}

const adjustCoinPriceFromUsdToCurrency = (coin: CoinPrices[0] | undefined): CoinPrices[0] | undefined => {

  if (!coin) return coin;

  const subCurrencyRate: number = (supportedQuoteCurrencies.includes(props.quote)) ? ((quoteCurrencyConvertions as any)[props.quote] ?? 1) : 1;

  return {
    symbol: coin.symbol,
    exchange: coin.exchange,
    close: (coin.close ?? 0) * subCurrencyRate,
    baseVolume: (coin.baseVolume ?? 0) * subCurrencyRate,
    quoteVolume: (coin.quoteVolume ?? 0) * subCurrencyRate,
    percentage: coin.percentage,
    vwap: (coin.vwap ?? 0) * subCurrencyRate,
  };

}

// Coin price in basic quote (e.g. USD) (TODO: use the one of the language if possible (e.g EUR for Italy) and fallback to USD if not exists)
const coinPriceInfo = adjustCoinPriceFromUsdToCurrency(coinPrices
    .find((coin: CoinPrices[0]) => {
      const symbol = normalizeUsd(coin.symbol)
      return symbol === `${props.crypto}/usd`.toLowerCase();
    }));

const listPricesForSymbolPerExchange = coinPrices
    .filter((coin: CoinPrices[0]) => {
      const symbol = normalizeUsd(coin.symbol)
      if (coin.exchange === 'any') {
        return false;
      }
      return symbol === `${props.crypto}/usd`.toLowerCase();
    })
    .filter((coin: CoinPrices[0], index: number, self: CoinPrices) => {
      return self.findIndex((t) => t.exchange === coin.exchange) === index;
    })
    .map(adjustCoinPriceFromUsdToCurrency);

const cryptoInfo: CoinMap[0] = getCoinInfoBySymbol(props.crypto);
const quoteInfo: CoinMap[0] = getCoinInfoBySymbol(props.quote);

const getConversionRate = async (quote: string): Promise<ConverterRateResponse> => {

  // 1. If quote is a "normal currency", get coinPriceInfo and use quote-currency-convertions.json to convert to that currency the value
  if (supportedQuoteCurrencies.includes(quote)) {
    const subCurrencyRate = (quoteCurrencyConvertions as any)[quote] ?? 1;
    return {
      "conversions": [
        {
          "from": props.crypto,
          "to": quote,
          "amount": 1,
          "rate": (coinPriceInfo?.close ?? 0) * subCurrencyRate,
          "total": (coinPriceInfo?.close ?? 0) * subCurrencyRate,
          "path": [
            props.crypto,
            quote
          ],
        }
      ]
    }
  }

  // throw new Error('Not implemented yet for symbol ' + props.crypto + '/' + quote);

  // 2. else, if it's a weird path, run the deep converter
  const response = await fetch('https://user.dipsway.cloud/prices/converter/any', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      from: props.crypto,
      to: quote,
      // amount: 1
    })
  });
  return await response.json();
};

const converterRate = await getConversionRate(props.quote);

</script>

<template>
  <div class="container flex relative items-center">
    <!-- Left Content (Main Content) -->
    <div class="flex flex-col items-center flex-1">
      <Callout
          data-aos
          class="duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5)"
          icon="i-hugeicons:calculate"
      >
        {{ $t('callout_calculator') }}
      </Callout>

      <div class="width-crop-mobile">
        <div data-aos
             class="heading-2 leading-[150%] text-start w-full mt-3 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{ cryptoInfo.name ?? crypto }} {{ $t('to') }} {{ quoteInfo.name ?? quote }} {{ $t('converter') }}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >

        </p>
        <div class="mt-10 text-center w-full">
          <CoinConverterRowCalculator
              :crypto="crypto"
              :quote="quote"
              :crypto-name="cryptoInfo.name"
              :quote-name="quoteInfo.name"
              :coin-price="converterRate?.conversions?.[0]?.rate"
              :coin-percentage="coinPriceInfo?.percentage"
              :coin-icon="cryptoInfo.img_url"
          />
        </div>
      </div>

      <div
          v-if="(converterRate?.conversions?.length??0) > 1"
          class="width-crop-mobile">

        <div data-aos
             class="heading-2 mt-30 leading-[150%] text-start w-full mt-10 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{(converterRate?.conversions?.length??2)}} {{ $t('different_ways_to_swap') }} {{ cryptoInfo.name ?? crypto }} {{ $t('to') }} {{ quoteInfo.name ?? quote }}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >
          {{ $t('coin_swap_description', { crypto, quote }) }}
        </p>

        <div class="mt-10">
          <CoinConverterRatePathList

              :quote="quote"
              :crypto="crypto"

              :quote-name="quoteInfo.name ?? quote"
              :crypto-name="cryptoInfo.name ?? crypto"

              :converter-rate="converterRate"
          />
        </div>
      </div>

      <div class="width-crop-mobile">

        <div data-aos
             class="heading-2 mt-30 leading-[150%] text-start w-full mt-10 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{ $t('info_on_price') }} {{ cryptoInfo.name ?? crypto }}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >
          {{ $t('description_calculator_pt_1') }} {{ cryptoInfo.name ?? crypto }} {{$t('description_calculator_pt_2') }}
          {{ quoteInfo.name ?? quote }}
        </p>

        <div class="mt-10">
          <CoinConverterPriceInfo
              :close="converterRate?.conversions?.[0]?.rate"
              :priceChange24h="coinPriceInfo?.percentage"
              :priceChange7d="coinPriceInfo?.percentage"
              :vwap="coinPriceInfo?.vwap"

              :baseVolume="coinPriceInfo?.baseVolume"
              :quoteVolume="coinPriceInfo?.quoteVolume"

              :quote="quote"
              :crypto="crypto"

              :quote-name="quoteInfo.name ?? quote"
              :crypto-name="cryptoInfo.name ?? crypto"
          />
        </div>
      </div>


      <div class="width-crop-mobile">

        <div data-aos
             class="heading-2 mt-30 leading-[150%] text-start w-full duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{ $t('live_data') }} {{ cryptoInfo.name ?? crypto }}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >
          {{ $t('description_calculator_pt_1') }} {{ cryptoInfo.name ?? crypto }} {{$t('description_calculator_pt_2') }}
          {{ quoteInfo.name ?? quote }}
        </p>

        <div class="mt-10">
          <CoinConverterPriceLabelTextInfo
              :close="coinPriceInfo?.close"
              :priceChange24h="coinPriceInfo?.percentage"
              :priceChange7d="coinPriceInfo?.percentage"
              :vwap="coinPriceInfo?.vwap"
              :baseVolume="coinPriceInfo?.baseVolume"
              :quoteVolume="coinPriceInfo?.quoteVolume"

              :quote="quote"
              :crypto="crypto"

              :quote-name="quoteInfo.name ?? quote"
              :crypto-name="cryptoInfo.name ?? crypto"
          />
        </div>
      </div>

      <div class="width-crop-mobile">

        <div data-aos
             class="heading-2 mt-10 leading-[150%] text-start w-full mt-30 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{ cryptoInfo.name ?? crypto }} {{ $t('supported_exchanges')}}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >
          {{$t('prices_of')}} {{ cryptoInfo.name ?? crypto }} {{ $t('on_different_exchanges') }}.
        </p>

        <div class="mt-10 w-full">
          <CoinConverterPriceTableInfo
              :quote="quote"
              :crypto="crypto"
              :quote-name="quoteInfo.name ?? quote"
              :crypto-name="cryptoInfo.name ?? crypto"
              :exchanges="listPricesForSymbolPerExchange"
          />
        </div>
      </div>

      <!-- TOOD: -->
      <!-- mostrare pre-converted 1 BTC to EUR 10 BTC to EUR 100 BTC to EUR (e per coin dal valore troppo alto 0.0001 BTC to EUR , 0.001 BTC to EUR e cosi via) -->

      <div class="width-crop-mobile">

        <div data-aos
             class="heading-2 mt-10 leading-[150%] text-start w-full mt-30 duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-200">
          {{ cryptoInfo.name ?? crypto }} {{ $t('converted_to') }} {{ quoteInfo.name ?? quote }}
        </div>
        <p
            data-aos
            :class="[
          'mt-5 leading-[150%] text-brand-secondary text-start w-full opacity-70',
          'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-5) delay-400'
        ]"
        >
          {{$t('prices_of')}} {{ cryptoInfo.name ?? crypto }} {{ $t('on_different_amounts') }}.
        </p>

        <div class="mt-10 w-full">
          <CoinConverterPriceConversionExamples
              :close="coinPriceInfo?.close"
              :priceChange24h="coinPriceInfo?.percentage"
              :priceChange7d="coinPriceInfo?.percentage"
              :vwap="coinPriceInfo?.vwap"
              :baseVolume="coinPriceInfo?.baseVolume"
              :quoteVolume="coinPriceInfo?.quoteVolume"

              :quote="quote"
              :crypto="crypto"

              :quote-name="quoteInfo.name ?? quote"
              :crypto-name="cryptoInfo.name ?? crypto"
          />
        </div>
      </div>

    </div>

    <!-- Right Content (Sticky Calculator) -->
    <div class="sticky-calculator-wrapper">
      <CoinConverterCalculator
          :crypto="crypto"
          :quote="quote"
          :converter-rate="converterRate"
      />
    </div>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row; /* Default direction for larger screens */
}

.sticky-calculator-wrapper {
  position: sticky;
  top: 5rem;
  right: 0;
  padding: 16px;
  z-index: 100; /* Ensures it stays above other elements */
}

.sticky-calculator-wrapper .CoinConverterCalculator {
  background: rgba(255, 255, 255, 0.1); /* Semi-transparent background for dark themes */
  border-radius: 8px; /* Rounded corners */
  border: 1px solid rgba(255, 255, 255, 0.2); /* Subtle border */
  backdrop-filter: blur(10px); /* Optional: Adds a blur effect behind the sticky element */
  padding: 16px;
}

.width-crop-mobile {
  width: 100%;
}

/* Responsive styles for mobile screens */
@media (max-width: 768px) {
  .container {
    flex-direction: column; /* Stack the calculator and content vertically on mobile */
    align-items: center; /* Center the content */
  }

  .width-crop-mobile {
    width: 20rem;
  }

  .sticky-calculator-wrapper {
    order: -1; /* Move the calculator to the top on mobile */
    position: static; /* Remove sticky behavior on mobile */
    width: 100%; /* Make it full-width on mobile */
    padding: 0; /* Remove padding to fit the mobile screen better */
    margin-bottom: 16px; /* Add some space below the calculator */
  }

  .sticky-calculator-wrapper .CoinConverterCalculator {
    width: 100%; /* Ensure the calculator fills the screen width */
    padding: 16px; /* Reapply padding to the content inside the calculator */
    margin: 0; /* Remove margins for full-width display */
  }
}

</style>